import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Spacer,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { DesktopOnly, MobileOnly } from "components/BreakpointVisibility";
import FilterButton from "components/FilterButton";
import FilterCard, { DateRangeFilter, FilterSet } from "components/FilterCard";
import { FilterStackProps } from "components/FilterStack";
import SearchBoxWithIcon from "components/SearchBoxWithIcon";
import { useRequestParams } from "contexts/RequestParamsProvider";
import useContractFilterOptions from "hooks/useContractFilterOptions";
import { RequestContractButton } from "routes/RequestContractPage";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import ContractListSort from "./ContractListSort";

const drawerPadding = 16;

const filterSidebarMaxWidth = mobileStyleBreakpoint("100%", "325px");

const minHeight = mobileStyleBreakpoint(`100%`, "unset");

const ContractListFiltersCard = ({
  selectStates,
  onClose = () => {
    return;
  },
}: Pick<FilterStackProps, "selectStates"> & {
  onClose?(): void;
  onClear?(): void;
}): React.ReactElement => {
  const { filters, onChangeDateRange, onChangeSelect, resetFilters } =
    useRequestParams();
  return (
    <FilterCard
      onClose={onClose}
      minHeight={minHeight}
      onClear={resetFilters}
      clearButtonText="Reset"
    >
      <Stack spacing={6}>
        <Stack spacing={4}>
          <DateRangeFilter
            filterKey="ship"
            onChange={onChangeDateRange}
            startDate={filters.shipStartDate as Date | undefined}
            endDate={filters.shipEndDate as Date | undefined}
          />
          <Divider />
        </Stack>
        <Stack spacing={8}>
          {selectStates.map((selectState) => (
            <FilterSet
              key={selectState.filterKey}
              filterKey={selectState.filterKey}
              heading={selectState.placeholder}
              options={selectState.options.map((option) => ({
                ...option,
                count: 0,
              }))}
              onChange={onChangeSelect}
              selectedOptions={
                (filters[selectState.filterKey] as string[] | undefined) ?? []
              }
            />
          ))}
        </Stack>
      </Stack>
    </FilterCard>
  );
};

const contractFiltersSpacing = mobileStyleBreakpoint<number>(6, 3);

const ContractFilters = ({
  contracts,
}: {
  contracts?: Contract[];
}): React.ReactElement => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { searchTerm, onChangeSearchTerm, hasSelectedFilters } =
    useRequestParams();

  const filterOptions = useContractFilterOptions(contracts);

  return (
    <Stack
      spacing={contractFiltersSpacing}
      width="100%"
      maxWidth={filterSidebarMaxWidth}
      minWidth={filterSidebarMaxWidth}
    >
      <Box>
        <SearchBoxWithIcon value={searchTerm} onChange={onChangeSearchTerm} />
      </Box>
      <DesktopOnly>
        <ContractListFiltersCard selectStates={filterOptions} />
      </DesktopOnly>
      <MobileOnly>
        <HStack spacing={0}>
          <FilterButton
            onClick={onOpen}
            hasSelectedFilters={hasSelectedFilters}
          />
          <Spacer />
          <Box maxW="160px">
            <ContractListSort />
          </Box>
          <Spacer />
          <RequestContractButton />
        </HStack>
        <Drawer isOpen={isOpen} onClose={onClose} size="full" placement="left">
          <DrawerOverlay />
          <DrawerContent h="full">
            <DrawerBody p={`${drawerPadding}px`}>
              <ContractListFiltersCard
                onClose={onClose}
                selectStates={filterOptions}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </MobileOnly>
    </Stack>
  );
};

export default ContractFilters;
